import { FC, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Chip, TableRow, TableCell } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { TfiApple, TfiEmail } from 'react-icons/tfi';
import { FcAndroidOs, FcGoogle } from 'react-icons/fc';
import { MdOutlineHorizontalRule } from 'react-icons/md';
import FacebookIcon from '@mui/icons-material/Facebook';
import { GrApple } from 'react-icons/gr';
import { BsBrowserChrome } from 'react-icons/bs';
import { UserList } from 'src/api/user/data/interfaces/users-list';


interface CustomStyledTableRowProps {
  user: UserList;
  businessNames?: Record<string, string>;
}

const DATE_FORMAT = 'dd, MMM yyyy HH:mm:ss z';

const StyledRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.background.default,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const Cell = styled(TableCell)({
  fontSize: 11,
  padding: 10,
});

const CenteredCell = styled(Cell)({
  textAlign: 'center',
});

const IconCell = styled(TableCell)({
  fontSize: 11,
  textAlign: 'center',
  padding: 0,
});

const PlatformIcon: FC<{ platform?: string }> = ({ platform }) => {
  switch (platform) {
    case 'ios':
      return <TfiApple fontSize="33" />;
    case 'android':
      return <FcAndroidOs fontSize="40" />;
    case 'web':
      return <BsBrowserChrome fontSize="30" />;
    case 'undefined':
      return <MdOutlineHorizontalRule fontSize="18" />;
    default:
      return null;
  }
};

const ProviderIcon: FC<{ provider?: string }> = ({ provider }) => {
  switch (provider) {
    case 'facebook.com':
      return <FacebookIcon color="primary" fontSize="large" />;
    case 'google.com':
      return <FcGoogle fontSize="33" />;
    case 'apple.com':
      return <GrApple fontSize="33" />;
    case 'password':
    case 'email':
      return <TfiEmail fontSize="33" />;
    default:
      return null;
  }
};

const CustomStyledTableRow: FC<CustomStyledTableRowProps> = ({
  user,
  businessNames
}) => {
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      event.preventDefault();
      if (user?.uid) {
        navigate(`/management/profile/details/${user.uid}`);
      }
    },
    [navigate, user?.uid]
  );

  const lastVisit = Array.isArray(user?.lastVisit)
    ? user.lastVisit.sort((a, b) => b - a)[0]
    : user?.lastVisit;

  const formatTimestamp = (timestamp: number | undefined, multiplier = 1000) => {
    if (!timestamp) return '--';
    try {
      return format(timestamp * multiplier, DATE_FORMAT);
    } catch {
      return '--';
    }
  };

  const formatLastVisit = () => {
    if (Number.isFinite(lastVisit)) {
      return format(lastVisit * 1000, DATE_FORMAT);
    }
    return `${lastVisit ?? '--'}`;
  };

  return (
    <StyledRow onClick={handleRowClick} hover>
      <Cell
        sx={{
          width: '18%',
          textDecoration: user?.userStatus ? 'line-through' : 'none',
        }}
      >
        <Box display="flex" flexDirection={'column'} alignItems="start" gap={0.75}>
          {user?.email}
          {user?.businessId && (
            <Chip
              label={businessNames?.[user.businessId] || 'Business'}
              size="small"
              color="primary"
              sx={{ fontSize: 9, height: 16, flexShrink: 0 }}
            />
          )}
        </Box>
      </Cell>

      {user?.platform ? (
        <IconCell sx={{ width: '12%' }}>
          <PlatformIcon platform={user.platform} />
        </IconCell>
      ) : (
        <Cell sx={{ width: '12%' }}>Undefined</Cell>
      )}

      <CenteredCell sx={{ width: '20%' }}>
        {user?.referralCode || '--'}
      </CenteredCell>

      <CenteredCell sx={{ width: '20%' }}>
        {user?.created
          ? formatTimestamp(user.created)
          : user?.createdOld || '--'}
      </CenteredCell>

      <CenteredCell sx={{ width: '20%' }}>{formatLastVisit()}</CenteredCell>

      <IconCell sx={{ width: '10%' }}>
        <ProviderIcon provider={user?.providers || user?.provider} />
      </IconCell>

      <CenteredCell sx={{ width: '20%' }}>
        {user?.countOfPurchases ?? 0}
      </CenteredCell>

      <CenteredCell sx={{ width: '20%' }}>
        ${(user?.amountOfPurchases ?? 0).toFixed(2)}
      </CenteredCell>
    </StyledRow>
  );
};

export default CustomStyledTableRow;
